import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ExportTarget, SkillMeta } from '../../types/skill';
import * as skillService from '../../services/skillService';

const TARGETS: { value: ExportTarget; label: string }[] = [
  { value: 'claude_code', label: 'Claude Code' },
  { value: 'codex', label: 'Codex / AGENTS.md' },
  { value: 'opencode', label: 'OpenCode' },
  { value: 'generic', label: 'Generic' },
];

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialSkillNames?: string[];
  availableSkills?: SkillMeta[];
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  isOpen,
  onClose,
  initialSkillNames = [],
  availableSkills = [],
}) => {
  const { darkMode } = useTheme();
  const [target, setTarget] = useState<ExportTarget>('claude_code');
  const [packName, setPackName] = useState('my-skill-pack');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(
    new Set(initialSkillNames)
  );

  if (!isOpen) return null;

  const toggleSkill = (name: string) => {
    setSelectedSkills(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleExport = async () => {
    if (!packName.trim() || selectedSkills.size === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      await skillService.exportPack({
        skill_names: Array.from(selectedSkills),
        target,
        pack_name: packName,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Export failed');
    } finally {
      setIsLoading(false);
    }
  };

  const skillsToShow = availableSkills.length > 0 ? availableSkills : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`w-full max-w-md mx-4 rounded-lg shadow-2xl border p-6 ${
        darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Export Skill Pack</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">✕</button>
        </div>

        {/* Skill selector */}
        <div className="mb-4">
          <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Skills ({selectedSkills.size} selected)
          </p>
          {skillsToShow.length > 0 ? (
            <div className={`max-h-48 overflow-y-auto rounded-md border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {skillsToShow.map((skill) => (
                <label
                  key={skill.name}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors ${
                    selectedSkills.has(skill.name)
                      ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSkills.has(skill.name)}
                    onChange={() => toggleSkill(skill.name)}
                    className="flex-shrink-0"
                  />
                  <span className="truncate">{skill.display_name}</span>
                  <span className={`text-xs ml-auto flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    ~{skill.estimated_tokens.toLocaleString()}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {Array.from(selectedSkills).map((name) => (
                <span key={name} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  {name}
                </span>
              ))}
              {selectedSkills.size === 0 && (
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No skills selected</span>
              )}
            </div>
          )}
        </div>

        {/* Target */}
        <div className="mb-4">
          <label className={`block text-xs font-medium uppercase tracking-wide mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Target Platform
          </label>
          <div className="grid grid-cols-2 gap-2">
            {TARGETS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTarget(t.value)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  target === t.value
                    ? 'bg-blue-500 text-white'
                    : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pack name */}
        <div className="mb-4">
          <label className={`block text-xs font-medium uppercase tracking-wide mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Pack Name
          </label>
          <input
            type="text"
            value={packName}
            onChange={(e) => setPackName(e.target.value)}
            className={`w-full px-3 py-2 rounded-md border text-sm ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isLoading || selectedSkills.size === 0 || !packName.trim()}
            className={`px-4 py-2 rounded-md text-sm text-white transition-colors ${
              isLoading || selectedSkills.size === 0
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {isLoading ? 'Downloading...' : 'Download .zip'}
          </button>
        </div>
      </div>
    </div>
  );
};
